import { useEffect, useState } from "react";
import { useApi } from "@/lib/api-client";

/** Who serves a model; the picker groups models by it (ADMIN-212). */
type ModelProvider = { id: string; name: string; logo?: string };

export type ModelOption = {
  id: string;
  name: string;
  description: string;
  provider?: ModelProvider;
  /** Reasoning effort levels the API sends for this model; absent when it has none. */
  efforts?: { id: string; name: string }[];
  keywords?: string[];
  disabled?: boolean;
  icon?: string;
};

const DEEPSEEK: ModelProvider = {
  id: "deepseek",
  name: "DeepSeek",
  logo: "https://models.dev/logos/deepseek.svg",
};
const OPENAI: ModelProvider = {
  id: "openai",
  name: "OpenAI",
  logo: "https://models.dev/logos/openai.svg",
};
const EFFORTS = [
  { id: "low", name: "Low" },
  { id: "medium", name: "Medium" },
  { id: "high", name: "High" },
];

/** Until `/api/config/models` answers (or when it can't): the same list the API falls back to. */
const FALLBACK_MODELS: ModelOption[] = [
  {
    id: "deepseek-v4-flash",
    name: "DeepSeek V4 Flash",
    description: "Fast and affordable",
    provider: DEEPSEEK,
    efforts: EFFORTS,
  },
  {
    id: "deepseek-v4-pro",
    name: "DeepSeek V4 Pro",
    description: "Most capable",
    provider: DEEPSEEK,
    efforts: EFFORTS,
  },
  { id: "gpt-4o", name: "GPT-4o", description: "OpenAI flagship", provider: OPENAI },
  { id: "gpt-4.1-nano", name: "GPT-4.1 Nano", description: "Fast and efficient", provider: OPENAI },
  {
    id: "o3-mini",
    name: "o3-mini",
    description: "OpenAI reasoning model",
    provider: OPENAI,
    efforts: EFFORTS,
  },
].map((m) => ({ ...m, icon: m.provider.logo }));

// v2: entries carry `provider` and `efforts` (ADMIN-212); the old cache had neither.
const CACHE_KEY = "kourai-models-cache-v2";
const CACHE_TTL = 60 * 60 * 1000;

interface CacheEntry {
  data: ModelOption[];
  expires: number;
}

function getCached(): ModelOption[] | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const entry: CacheEntry = JSON.parse(raw);
    if (Date.now() < entry.expires) return entry.data;
  } catch {}
  return null;
}

function setCached(models: ModelOption[]) {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ data: models, expires: Date.now() + CACHE_TTL }),
    );
  } catch {}
}

export function useModels(): ModelOption[] {
  const client = useApi();
  const [models, setModels] = useState<ModelOption[]>(() => getCached() ?? FALLBACK_MODELS);

  useEffect(() => {
    let cancelled = false;

    client.api.config.models
      .$get()
      .then(async (res) => {
        if (cancelled) return;
        if (!res.ok) throw new Error("fetch failed");
        const data = (await res.json()) as ModelOption[];
        setCached(data);
        setModels(data);
      })
      .catch(() => {
        if (cancelled) return;
        const cached = getCached();
        if (!cached) setModels(FALLBACK_MODELS);
      });

    return () => {
      cancelled = true;
    };
  }, [client]);

  return models;
}
